import React, { useCallback, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { FormattedMessage, useIntl } from 'react-intl';

import { Routes } from '@share/constants';
import { BlueButton } from '@share/components';
import {
  CertificatePaymentModeEnum,
  ItemTypeEnum,
  type ICertificateItemTemplate,
  type ICertificatePaymentSuccessState,
  type ICertificatePurchaseResult,
} from '@share/common-types';
import { FunctionalRootState, eligibleCruiseLineNames, isPublicClubSiteAnonymousVisitor } from '@share/utils';
import { fetchCertificateTemplateById } from '@share/store/slices';
import type { ILoginState } from '@share/store/slices';
import { useCertificateClubSiteId } from '@share/components';

import { CertificateCard } from '../components/certificate-card';
import { CertificatePaymentForm } from '../components';

import styles from './styles.module.scss';
import { Spin } from 'antd';

const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

export function CertificatePaymentWrapper(): React.ReactElement {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();
  const { id, accountUsername } = useParams<{ id: string; accountUsername?: string }>();
  const { certificateTemplate, loading, error } = useSelector(
    (state: FunctionalRootState) => state.CertificateTemplateByIdStore,
  );
  const { account } = useSelector((state: FunctionalRootState) => state.loginStore) as ILoginState;
  const requiresInlineSignup = isPublicClubSiteAnonymousVisitor(account);
  const organizationId = useCertificateClubSiteId();

  const isCruiseCertificate = certificateTemplate?.itemTemplates.some(
    (item: ICertificateItemTemplate) => item.type === ItemTypeEnum.Cruise,
  );
  const cruiseLineNames = eligibleCruiseLineNames(certificateTemplate?.eligibleCruiseLines);

  const certificatesPath = accountUsername
    ? `/${accountUsername}${Routes.CertificateTemplateDetails}`
    : Routes.CertificateTemplateDetails;

  const returnUrl = `${window.location.origin}${accountUsername ? `/${accountUsername}` : ''}${
    Routes.Certificates
  }`;

  useEffect(() => {
    if (id) {
      dispatch(fetchCertificateTemplateById({ id, clubSiteId: organizationId }));
    }
  }, [dispatch, id, organizationId]);

  const handleBackClick = useCallback(() => {
    history.push(`${certificatesPath}/${id}`);
  }, [history, certificatesPath, id]);

  const successPath = accountUsername
    ? `/${accountUsername}${Routes.CertificatesPaymentSuccess}`
    : Routes.CertificatesPaymentSuccess;

  const handlePurchaseSuccess = useCallback(
    (
      purchaseResult: ICertificatePurchaseResult,
      context?: { paymentMode: CertificatePaymentModeEnum; instalmentPlanMonths?: number },
    ) => {
      if (!certificateTemplate) {
        return;
      }
      const state: ICertificatePaymentSuccessState = {
        purchaseResult,
        certificateTemplate: certificateTemplate,
        paymentMode: context?.paymentMode,
        instalmentPlanMonths: context?.instalmentPlanMonths,
      };
      history.push(successPath, state);
    },
    [history, successPath, certificateTemplate],
  );

  const backLabel = intl.formatMessage({ id: 'certificates.backTo' });

  if (loading) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.contentFlex}>
          <div className={styles.stateBlock}>
            <Spin size="large" />
            <span>
              <FormattedMessage id="certificates.loading" />
            </span>
          </div>
        </div>
      </div>
    );
  }

  const isNotAvailable = error?.code === 'certificate_unavailable';

  if (error || !certificateTemplate) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.contentFlex}>
          <a
            href={certificatesPath}
            className={styles.backLink}
            onClick={(e) => {
              e.preventDefault();
              handleBackClick();
            }}
            aria-label={backLabel}
          >
            {backLabel}
          </a>
          <div className={styles.stateBlock}>
            <p className={styles.errorText}>
              {isNotAvailable
                ? intl.formatMessage({ id: 'certificates.noLongerAvailable' })
                : error?.title ?? intl.formatMessage({ id: 'certificates.notFound' })}
            </p>
            {!isNotAvailable && <BlueButton onClick={handleBackClick}>{backLabel}</BlueButton>}
          </div>
        </div>
      </div>
    );
  }

  if (!stripePromise) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.contentFlex}>
          <div className={styles.stateBlock}>
            <p className={styles.errorText}>
              <FormattedMessage id="certificates.paymentNotConfigured" />
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        <div className={styles.formColumn}>
          <a
            href={certificatesPath}
            className={styles.backLink}
            onClick={(e) => {
              e.preventDefault();
              handleBackClick();
            }}
            aria-label={backLabel}
          >
            {backLabel}
          </a>
          <h1 className={styles.pageTitle}>
            <FormattedMessage id="certificates.payment.pageTitle" />
          </h1>
          <p className={styles.pageSubtitle}>
            <FormattedMessage id="certificates.payment.pageSubtitle" />
          </p>

          <Elements stripe={stripePromise}>
            <CertificatePaymentForm
              certificateTemplate={certificateTemplate}
              returnUrl={returnUrl}
              stripePromise={stripePromise}
              onSuccess={handlePurchaseSuccess}
              redeemedruiseLines={
                isCruiseCertificate && cruiseLineNames.length > 0 ? cruiseLineNames : undefined
              }
              requiresInlineSignup={requiresInlineSignup}
            />
          </Elements>
        </div>

        <aside className={styles.stickyCardColumn}>
          <CertificateCard
            item={certificateTemplate}
            imageUrl={certificateTemplate.imageUrl ?? undefined}
            variant="summary"
            summaryTaxAmount={null}
          />
        </aside>
      </div>
    </div>
  );
}
